import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import useQuery from 'hooks/useQuery'
import { useLocation, useNavigate } from 'react-router-dom'
import userStorage from 'services/userStorage'
import skipWaitingRoomStorage from 'services/skipWaitingRoomStorage'
import { toastContext } from 'context/ToastContext'
import Turnstile from 'components/Turnstile'
import { getQrCountryFromSearch, QR_COUNTRIES } from 'helpers/qrCountry'

export default function QRLogin() {
  const { search } = useLocation()
  const navigate = useNavigate()
  const { setToastOptions } = useContext(toastContext)
  const [verificationError, setVerificationError] = useState(false)
  const [resetSignal, setResetSignal] = useState(0)
  const loginInProgress = useRef(false)

  const code = useMemo(() => {
    try {
      return new URLSearchParams(search).get('code')
    } catch (_err) {
      return search.slice(1)
    }
  }, [search])
  const country = useMemo(() => getQrCountryFromSearch(search), [search])
  const isChile = country === QR_COUNTRIES.CHILE

  const { makeRequest } = useQuery({
    defaultMethod: 'post',
    isControlled: true,
    toastOptions: { showFeedbackMessage: false },
    disableAutoLogoutOn401: true,
  })

  const loginWithQRCode = useCallback(
    async (turnstileToken) => {
      if (loginInProgress.current || !code) return
      loginInProgress.current = true
      try {
        const response = await makeRequest({
          body: { code, turnstileToken },
          endpoint: '/login-qr',
        })
        if (response.error) throw new Error(response.message)
        await userStorage.saveUser({ ...response.data, country })
        await skipWaitingRoomStorage.saveSkipWaitingRoom(true)
        navigate('/accounts')
      } catch (e) {
        loginInProgress.current = false
        setVerificationError(true)
        setToastOptions({
          message: isChile
            ? 'No pudimos verificar el ingreso. Intenta nuevamente'
            : 'No pudimos verificar el ingreso. Intentá nuevamente',
          error: true,
        })
      }
    },
    [code, country, isChile, makeRequest, navigate, setToastOptions],
  )

  const handleVerificationError = useCallback(() => {
    loginInProgress.current = false
    setVerificationError(true)
  }, [])

  const retryVerification = () => {
    setVerificationError(false)
    setResetSignal((value) => value + 1)
  }

  if (!code) {
    return (
      <Stack minHeight='70vh' alignItems='center' justifyContent='center' px={3}>
        <Typography variant='h6'>Este código QR no es válido</Typography>
      </Stack>
    )
  }

  return (
    <Stack minHeight='70vh' alignItems='center' justifyContent='center' spacing={3} px={3}>
      {verificationError ? (
        <>
          <Typography variant='h6' textAlign='center'>No pudimos verificar el ingreso</Typography>
          <Button variant='contained' startIcon={<RefreshIcon />} onClick={retryVerification}>
            Intentar nuevamente
          </Button>
        </>
      ) : (
        <>
          <CircularProgress size={34} aria-label='Verificando ingreso' />
          <Typography variant='h6' textAlign='center'>
            {isChile
              ? 'Verificando que eres una persona...'
              : 'Verificando que sos una persona...'}
          </Typography>
        </>
      )}
      <Turnstile
        onVerify={loginWithQRCode}
        onExpire={handleVerificationError}
        onError={handleVerificationError}
        resetSignal={resetSignal}
      />
    </Stack>
  )
}
